import moment from 'moment';
import i18n, { getDateFormatOptions } from './i18n';
import { Currency, DEFAULT_CURRENCY, CurrencyById } from './price';
import { dayOfWeek } from './time';
moment.locale('de');

export function formatPrice(
  price = 0,
  extras,
  number = false,
  handleSign = false,
  currency = '€',
) {
  let total = price;
  if (extras) {
    extras.forEach((extra) => {
      if (extra.is_selected) {
        total += extra.price;
      }
    });
  }
  if (number) {
    return total;
  }

  const isNegative = Math.sign(total) === -1;

  const formattedTotal = `${(Number(Math.abs(total)) / 100)
    .toFixed(2)
    .replace('.', ',')} ${currency}`;

  if (handleSign) {
    return `${isNegative ? '- ' : '+'} ${formattedTotal}`;
  }

  return formattedTotal;
}

/**
 * Format a date and time to a localized string including timezone
 * @param {Date} date - The date to format
 * @param {string} language - The i18n.language to format the date in
 * @returns {string} The formatted date and time
 */
export function formatDateTimeLocalized(date, language) {
  return new Date(date).toLocaleDateString(
    language,
    getDateFormatOptions(true),
  );
}

export function formatDate(isoDate) {
  return moment(isoDate).format('DD MMM YYYY');
}

export function formatShortDate(isoDate) {
  return moment(isoDate).format('DD.MM.YYYY');
}

export function formatDiscount(campaign) {
  if (campaign.discount_type === discountTypes.FULL_AMOUNT_DISCOUNT) {
    return formatPrice(campaign.discount_amount);
  }
  return `${campaign.discount_percent / 10} %`;
}

export function formatPercent(number) {
  return `${number / 10} %`;
}

export function formatDateSmall(isoDate, labeled, withWeekday) {
  const date = moment(isoDate);
  if (labeled) {
    const weekday = withWeekday ? `${dayOfWeek[date.isoWeekday()]} ` : '';
    return `${weekday}${date.format('DD.MM.')}`;
  }
  return date.format('DD.MM.');
}

export function formatTime(isoDate) {
  if (isoDate.format === 'minutes') {
    return isoDate.label;
  }
  if (isoDate.format) {
    return `${isoDate.label}`;
  }
  return `${moment(isoDate).format('HH:mm')}`;
}

export function formatBillomatPrice(price, currencySymbol) {
  return formatPrice(Number(price) * 100, null, false, false, currencySymbol);
}

export const calcDiscount = (total, discount = 0) => (total * discount) / 1000;

export const calcTotal = (
  total,
  discount = 0,
  fee = 0,
  blockCampaigns = false,
) => {
  if (blockCampaigns) {
    return Math.round(total + fee);
  }
  return Math.round(total + fee - calcDiscount(total, discount));
};

export const centsToPrice = (cents, currencyIn, maximumFractionDigits = 2) => {
  const currency = Currency[currencyIn?.toUpperCase()] || DEFAULT_CURRENCY;
  if (isNaN(Number(cents))) {
    cents = 0;
  }
  return (cents / 100).toLocaleString(i18n.language, {
    style: 'currency',
    currency: currency,
    maximumFractionDigits,
  });
};

export function costToPrice(cost) {
  return centsToPrice(
    cost.amount * 100,
    CurrencyById[cost.currencyId] || CurrencyById[0],
  );
}

export function formatShortTime(isoDate) {
  if (isoDate.format === 'minutes') {
    return isoDate.label;
  }
  if (isoDate.format) {
    return `${isoDate.label}`;
  }
  return `${moment(isoDate).format('HH:mm')}`;
}

export function formatBasketItemInput(values) {
  return {
    product_id: values.product_id,
    name: values.name,
    price: values.price,
    variant_id: values.variant_id,
    extras: values.extras
      ? values.extras.map((extra) => ({
          extra_id: extra.extra_id,
          name: extra.name,
          price: extra.price,
          is_selected: extra.is_selected,
        }))
      : null,
    comment: values.comment || null,
  };
}

export const campaignTypes = {
  INDIVIDUAL_USER_CAMPAIGN: 'INDIVIDUAL_USER_CAMPAIGN',
  STORE_CAMPAIGN: 'STORE_CAMPAIGN',
  BRAND_CAMPAIGN: 'BRAND_CAMPAIGN',
  NO_ORDER_FEE_CAMPAIGN: 'NO_ORDER_FEE_CAMPAIGN',
  DISCOUNT_PERCENT: 'DISCOUNT_PERCENT',
  DEFAULT: 'DEFAULT',
};

export const discountTypes = {
  FULL_AMOUNT_DISCOUNT: 'FULL_AMOUNT_DISCOUNT',
  BASKET_PERCENT_DISCOUNT: 'BASKET_PERCENT_DISCOUNT',
  NO_ORDER_FEE_DISCOUNT: 'NO_ORDER_FEE_DISCOUNT',
};

export function formatTotal(basket) {
  return basket ? ` ${formatPrice(basket.total)}` : '';
}

export function camelToSnakeCase(str) {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}
